<template>
  <div>
    <project-toolbar :selectMonth="true">
      <md-menu
        md-align-trigger
        md-size="small"
        class="box-right-edge"
        style="border-left: none"
      >
        <md-button md-menu-trigger>{{ L('download') }}</md-button>
        <md-menu-content>
          <md-menu-item @click="download('xlsx')">XLSX</md-menu-item>
          <md-menu-item @click="download('csv')">CSV</md-menu-item>
        </md-menu-content>
      </md-menu>
      <search-field v-model="search" :fields="searchFields" />
    </project-toolbar>
    <md-content>
      <md-table
        v-model="usageDataFiltered"
        :md-sort.sync="sort.sort"
        :md-sort-order.sync="sort.sortOrder"
        md-stripe
        md-fixed-header
        :md-height="window.height"
        ref="dataTable"
      >
        <md-table-empty-state :md-label="C('noData')" />
        <md-table-row slot="md-table-row" slot-scope="{ item }">
          <md-table-cell
            v-if="$config.feature.project"
            :md-label="L('project')"
            md-sort-by="project_name"
            >{{ item.project_name }}</md-table-cell
          >
          <md-table-cell :md-label="L('service')">{{
            item.service_name
          }}</md-table-cell>
          <md-table-cell :md-label="L('product')">{{
            item.sku_name
          }}</md-table-cell>
          <md-table-cell :md-label="L('region')">{{
            item.region
          }}</md-table-cell>
          <md-table-cell :md-label="L('term')">
            {{ item.usage_start_date | moment_format('MM-DD') }} ~
            {{ item.usage_end_date | moment_format('MM-DD') }}
          </md-table-cell>
          <md-table-cell
            v-if="$config.feature.pricingUnit"
            :md-label="L('usage')"
            md-numeric
            >{{
              Math.floor(item.usage * 10000000000) / 10000000000
            }}</md-table-cell
          >
          <md-table-cell
            v-if="$config.feature.pricingUnit"
            :md-label="L('usageUnit')"
            md-numeric
            >{{ item.usage_unit }}</md-table-cell
          >
          <md-table-cell
            v-if="!$config.feature.pricingUnit"
            :md-label="L('usage')"
            md-numeric
            >{{ item.usage }}</md-table-cell
          >
          <md-table-cell
            v-if="!$config.feature.pricingUnit"
            :md-label="L('usageUnit')"
            md-numeric
            >{{
              item.usage_unit === 'null' ? '' : item.usage_unit
            }}</md-table-cell
          >

          <md-table-cell :md-label="L('cost')" md-numeric md-sort-by="cost">{{
            Number(item.cost) | currency(displayCurrency)
          }}</md-table-cell>

          <md-table-cell
            :md-label="L('discount')"
            md-numeric
            md-sort-by="credit"
            >{{
              Number(item.credit) | currency(displayCurrency)
            }}</md-table-cell
          >

          <md-table-cell
            :md-label="L('total')"
            md-numeric
            md-sort-by="final_cost"
            >{{
              Number(item.final_cost) | currency(displayCurrency)
            }}</md-table-cell
          >
        </md-table-row>
      </md-table>
    </md-content>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import alasql from 'alasql'
import moment from 'moment'
import 'file-saver'
import XLSX from 'xlsx'
import api from '@/api'
import SearchField from '@/views/layout/SearchField'
import ProjectToolbar from '@/views/billing/ProjectToolbar'
import { propFilter } from '@/lib/helper'
import { getExchangeRate } from '@/lib/exchangeRate'
import { convertCosts, getUserProjectPermissions } from '@/lib/utils'

export default {
  components: { ProjectToolbar, SearchField },

  data: () => ({
    search: '',
    searchFields: {
      project_name: 'project',
      service_name: 'service',
      sku_name: 'product',
    },
    sort: {
      sort: 'final_cost',
      sortOrder: 'desc',
    },
    window: {
      height: 0,
    },
    monthlyData: [],
  }),

  created() {
    if (!this.$config.feature.project) {
      delete this.searchFields.project_name
    }
  },

  mounted() {
    this.load()
  },

  updated() {
    this.$nextTick(() => {
      if (this.usageData && this.usageData.length > 0) {
        window.dispatchEvent(new Event('resize'))
      }
    })
  },

  computed: {
    ...mapState({
      curAccount: (state) => state.curAccount,
      curMonth: (state) => state.curMonth,
      exchangeRateList: (state) => state.exchangeRateList,
      displayCurrency: (state) => state.displayCurrency,
      userInfo: (state) => state.userInfo,
    }),

    usageDataFiltered: {
      get() {
        return propFilter(
          this.search,
          this.usageData,
          Object.keys(this.searchFields)
        )
      },
      set() {},
    },

    usageData() {
      const usePricingUnit = this.$config.feature.pricingUnit
      const monthlyData = this.monthlyData.reduce((arr, item) => {
        const obj = Object.assign({}, item)
        const { cost, credit } = this.convertCosts([item], this.curMonth)
        obj.cost = cost
        obj.credit = credit
        obj.currency = this.displayCurrency
        obj.usage_start_date = moment(obj.usage_start_date).format('YYYY-MM-DD')
        obj.usage_end_date = moment(obj.usage_end_date).format('YYYY-MM-DD')
        arr.push(obj)
        return arr
      }, [])

      let rate = 1
      if (this.displayCurrency !== 'KRW') {
        rate = this.getExchangeRate(this.displayCurrency, {
          month: this.curMonth,
        })
      }

      const list = alasql(
        `
          select *
          from (
            select
              project_id,
              project_name,
              service_id,
              service_name,
              sku_id,
              sku_name,
              region,
              currency,
              cost_type,
              ${
                usePricingUnit ? 'usage_pricing_unit' : 'usage_unit'
              } usage_unit,
              min(usage_start_date) usage_start_date,
              max(usage_end_date) usage_end_date,
              sum(${usePricingUnit ? 'usage_in_pricing_unit' : 'usage'}) usage,
              sum(cost) cost,
              sum(credit) credit,
              sum(cost + credit) final_cost
            from ?
            group by project_id, project_name, service_id, service_name, sku_id, sku_name, region, currency, cost_type,
              ${usePricingUnit ? 'usage_pricing_unit' : 'usage_unit'}
          ) A
          where abs(cost * ${rate}) > 0.005
        `,
        [monthlyData]
      )

      list.sort((a, b) => b.cost - a.cost)
      return list
    },
  },

  watch: {
    curAccount() {
      this.load()
    },

    curMonth() {
      this.load()
    },
  },

  methods: {
    async load() {
      if (!this.curAccount.id) return
      await getExchangeRate(this.curMonth)

      const userProjectPermissions = getUserProjectPermissions(
        this.userInfo.resource,
        this.curAccount
      )
      this.monthlyData = await api.usage.monthly(
        this.curAccount.id,
        this.curMonth,
        { projects: userProjectPermissions }
      )
    },

    getExchangeRate(currency, context) {
      const er = this.exchangeRateList.find(
        (e) => e.month === context.month && e.code === currency
      )
      return er ? er.send_rate : { finalCost: 0 }
    },

    convertCosts(costs, thisMonth) {
      const month = thisMonth || this.curMonth
      return convertCosts(
        costs,
        this.displayCurrency,
        this.getExchangeRate.bind(this),
        { month },
        null
      )
    },

    getBytesUnit(bytes) {
      const units = [
        'byte',
        'kibibyte',
        'mebibyte',
        'gibibyte',
        'tebibyte',
        'pebibyte',
      ]
      const scale = Math.max(
        Math.min(Math.floor(Math.log2(bytes) / 10), units.length - 1),
        0
      )
      return {
        unit: units[scale],
        scale: 1024 ** scale,
      }
    },

    getMonthDays() {
      return moment(`${this.curMonth}-01`, 'YYYY-MM-DD').endOf('month').date()
    },

    async download(fileType) {
      let cols = {
        service_id: 'Service ID',
        service_name: 'Service Name',
        sku_id: 'SKU ID',
        sku_name: 'SKU Name',
        region: 'Region',
        currency: 'Currency',
        usage_unit: 'Usage Unit',
        usage_start_date: 'Start Date',
        usage_end_date: 'End Date',
        usage: 'Usage',
        cost_type: 'Cost Type',
        cost: 'Cost',
        credit: 'Credit',
        final_cost: 'Final Cost',
      }

      if (this.$config.feature.project) {
        cols = Object.assign(
          {
            project_id: 'Project ID',
            project_name: 'Project Name',
          },
          cols
        )
      }

      const data = [
        Object.values(cols),
        ...this.usageData.map((agg) =>
          Object.keys(cols).map((col) => agg[col])
        ),
      ]

      const wb = XLSX.utils.book_new()

      XLSX.utils.book_append_sheet(
        wb,
        XLSX.utils.aoa_to_sheet(data),
        this.curMonth
      )
      XLSX.writeFile(
        wb,
        `Usage Detail_${this.curAccount.name}_${this.curMonth}.${fileType}`
      )
    },

    windowResized() {
      this.window.height = window.innerHeight - 200
    },
  },
}
</script>

<style lang="scss" scoped>
.md-content {
  padding: 8px;
}
</style>
